/**
 * Locations Management Page - Notion-Inspired Design
 * Manage business locations and QR codes
 */

import React, { useEffect, useState } from 'react';
import { locationsApi, Location } from '../lib/api';
import { Button, Input, Card, Badge, Modal, ModalBody, ModalFooter, toast } from '../components/ui';

export const LocationsPage: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    country: 'United States',
    zipCode: '',
    phoneNumber: '',
  });

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    setIsLoading(true);
    try {
      const response = await locationsApi.getAll();
      setLocations(response.data.data);
    } catch (error) {
      console.error('Failed to load locations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const addressParts = [formData.address, formData.city, formData.state, formData.zipCode, formData.country].filter(Boolean);
      const payload: { name: string; address: string; phoneNumber?: string } = {
        name: formData.name,
        address: addressParts.join(', '),
      };
      if (formData.phoneNumber) payload.phoneNumber = formData.phoneNumber;
      await locationsApi.create(payload);
      setShowAddModal(false);
      setFormData({
        name: '',
        address: '',
        city: '',
        state: '',
        country: 'United States',
        zipCode: '',
        phoneNumber: '',
      });
      await loadLocations();
      toast.success('Location added successfully!');
    } catch (error) {
      console.error('Failed to add location:', error);
      toast.error('Failed to add location. Please try again.');
    }
  };

  const handleToggleActive = async (locationId: string) => {
    try {
      await locationsApi.toggleActive(locationId);
      await loadLocations();
      toast.success('Location status updated.');
    } catch (error) {
      console.error('Failed to toggle location:', error);
      toast.error('Failed to update location status.');
    }
  };

  const handleDelete = async (locationId: string) => {
    if (!confirm('Are you sure you want to delete this location?')) return;

    try {
      await locationsApi.delete(locationId);
      await loadLocations();
      toast.success('Location deleted.');
    } catch (error) {
      console.error('Failed to delete location:', error);
      toast.error('Failed to delete location.');
    }
  };

  const handleDownloadQR = async (location: Location) => {
    try {
      const response = await locationsApi.getQRCode(location.id);
      const link = document.createElement('a');
      link.href = response.data.data.qrCodeUrl;
      link.download = `${location.name.replace(/\s+/g, '-')}-QR.png`;
      link.click();
    } catch (error) {
      console.error('Failed to download QR code:', error);
      toast.error('Failed to download QR code.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-h1 font-bold text-black">Locations</h1>
            <p className="text-base mt-2 text-black-lighter">
              Manage your business locations and check-in QR codes
            </p>
          </div>
        </div>
        <Card>
          <div className="h-32 bg-cream-100 rounded animate-pulse"></div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-h1 font-bold text-black">
            Locations
          </h1>
          <p className="text-base mt-2 text-black-lighter">
            Manage your business locations and check-in QR codes
          </p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          variant="primary"
          size="md"
          className="whitespace-nowrap self-start sm:self-auto"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Location
        </Button>
      </div>

      {/* Locations - Empty State */}
      {locations.length === 0 ? (
        <Card>
          <div className="text-center py-16 px-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-lg flex items-center justify-center bg-blue-100">
              <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-h4 font-semibold mb-2 text-black">
              No locations yet
            </h3>
            <p className="text-base mb-6 max-w-md mx-auto text-black-lighter">
              Add your first location to start accepting check-ins and tracking customer visits
            </p>
            <Button
              onClick={() => setShowAddModal(true)}
              variant="primary"
              size="md"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Add Your First Location
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid gap-6">
          {locations.map((location) => (
            <Card key={location.id} data-testid={`location-card-${location.id}`}>
              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="text-h4 font-semibold text-black" data-testid={`location-name-${location.id}`}>
                        {location.name}
                      </h3>
                      <Badge variant={location.isActive ? 'success' : 'default'}>
                        {location.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-base text-black-light">
                      {location.address}
                    </p>
                    {location.phoneNumber && (
                      <p className="text-base mt-1 text-black-lighter">
                        {location.phoneNumber}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      onClick={() => handleDownloadQR(location)}
                      variant="secondary"
                      size="sm"
                    >
                      Download QR
                    </Button>
                    <Button
                      onClick={() => handleToggleActive(location.id)}
                      variant="secondary"
                      size="sm"
                      data-testid={`location-toggle-${location.id}`}
                    >
                      {location.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      onClick={() => handleDelete(location.id)}
                      variant="tertiary"
                      size="sm"
                      className="text-error-600 hover:bg-error-50"
                      data-testid={`location-delete-${location.id}`}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* How Check-ins Work */}
      <Card>
        <div className="p-6">
          <h2 className="text-h3 font-semibold mb-4 text-black">
            How Check-ins Work
          </h2>
          <div className="grid gap-4">
            {[
              {
                step: '1',
                title: 'Download QR Code',
                description: 'Download the QR code for each location from above',
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                ),
              },
              {
                step: '2',
                title: 'Display at Location',
                description: 'Print and display the QR code prominently at your location',
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                ),
              },
              {
                step: '3',
                title: 'Customers Check In',
                description: 'Customers scan the QR code with their mobile app',
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                ),
              },
              {
                step: '4',
                title: 'Automatic Rewards',
                description: 'Customers automatically earn Stars tokens on purchase',
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ),
              },
            ].map((item) => (
              <div key={item.step} className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-blue-100 text-blue-500">
                  {item.icon}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-base mb-1 text-black">
                    {item.title}
                  </h4>
                  <p className="text-base text-black-lighter">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Add Location Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Location"
        size="lg"
      >
        <form onSubmit={handleAddLocation}>
          <ModalBody>
            <p className="text-base text-black-lighter mb-5">
              Create a new business location for customer check-ins
            </p>
            <div className="grid gap-5">
              <Input
                label="Location Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Main Store"
                isRequired
              />

              <Input
                label="Street Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St"
                isRequired
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="City"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="San Francisco"
                  isRequired
                />
                <Input
                  label="State"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="CA"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  isRequired
                />
                <Input
                  label="ZIP Code"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  placeholder="94102"
                  isRequired
                />
              </div>

              <Input
                label="Phone Number"
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                placeholder="(415) 555-1234"
              />
            </div>
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              onClick={() => setShowAddModal(false)}
              variant="secondary"
              size="md"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              className="flex-1"
              data-testid="modal-add-location-btn"
            >
              Add Location
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};
